// src/pages/fleet/CreateTripPage.tsx — create a new trip (full dispatch form).
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getVehicles, createTrip, updateTrip, getTrip, getVendors, geocodeAddress, isInIndia } from '../../services/fleetApi';
import type { Vendor } from '../../services/fleetApi';
import { P } from './fleetStyles';
import Icon, { IconName } from './Icon';
import useIsMobile from './useIsMobile';
import { css } from '../../utils/css';
import { providerName } from './gpsProvider';
import axios from 'axios';
import type { ApiErrorBody, ISODate, Vehicle } from '../../types';

const DISPATCH_TYPES = ['Retailer', 'Dealer'];

/** "Telangana (36)" — or a plain dash before a dealer has been chosen. */
const destLabel = (name: string, code: string) =>
  (code ? `${name ? `${name} ` : ''}(${code})`.trim() : '—');

// Every dispatch leaves from the same plant, so the pickup point is fixed and
// the operator never types it (or its coordinates) again.
const PICKUP = {
  location: 'Bagging Building, Fertilizer Corporation Of India, Godavarikhani, Ramagundam, Telangana, India',
  lat: '18.77850',
  lng: '79.50310',
};

// Plant-level codes that are identical on every dispatch — never typed or edited.
const FIXED_CODES = {
  companyCode:   '100353',
  plantPortCode: '10035301',
  productCode:   '1089',
};

// Form state. Inputs produce strings; coordinates/quantity may also hold the
// numbers loaded from an existing trip in edit mode. A `type` (not interface)
// so it is assignable to the API's FormBody index signature.
type TripForm = {
  tripName: string; vehicleId: string; driverName: string; driverMobile: string;
  estStart: string; estEnd: string;
  fromLocation: string; fromLat: string | number; fromLng: string | number;
  toLocation: string; toLat: string | number; toLng: string | number;
  bagsCount: number;
  challanCode: string; dispatchType: string; dispatchCode: string;
  waybillNumber: string; invoiceNumber: string; quantityMt: string | number;
  companyCode: string; plantPortCode: string; productCode: string;
  ddCode: string; dealerCode: string; destStateCode: string; destDistrictCode: string;
  destStateName: string; destDistrictName: string;
};
/** Form fields an <input>/<select> can write its string value into. */
type TextField = { [K in keyof TripForm]: string extends TripForm[K] ? K : never }[keyof TripForm];

/**
 * Being on the IFMS whitelist says IFMS will accept the trip; it says nothing about
 * whether the provider actually sends us this truck's position. A whitelisted truck
 * with no GPS feed would sit PENDING for ever — never auto-started, never reported.
 *
 * Silence is not by itself a fault, though: a truck parked for a few hours, a device
 * switched off overnight or a patch with no network all look the same from here. So
 * only two cases are called out, and only the first is treated as a fault:
 *   never  — not one position, ever → the provider has not enabled this truck for us
 *   quiet  — nothing for days, though it has reported before → probably parked
 * Anything more recent is left alone; once the trip is running the GPS_GAP,
 * DEVICE_OFFLINE and DELAYED_DATA alerts watch the feed properly.
 */
const GPS_QUIET_HOURS = 72;
type GpsState = { level: 'never' | 'quiet'; label: string };
function gpsState(v: Vehicle): GpsState | null {
  const at = v.lastGpsAt || v.lastPingAt;
  if (!at) return { level: 'never', label: 'no GPS ever' };
  const hrs = (Date.now() - new Date(at).getTime()) / 3600000;
  if (!Number.isFinite(hrs) || hrs < GPS_QUIET_HOURS) return null;
  return { level: 'quiet', label: `quiet ${Math.round(hrs / 24)} days` };
}

/** A lot of the dealer sheet has no survey done yet, so coordinates may be missing or 0/0. */
function hasCoords(v: Vendor): boolean {
  return v.latitude != null && v.longitude != null && !(v.latitude === 0 && v.longitude === 0);
}

/** One line for the dealer: "Name, District, State" — whatever the master actually holds. */
function vendorPlace(v: Vendor): string {
  return [v.name, v.districtName, v.stateName].filter(Boolean).join(', ');
}

const BLANK: TripForm = {
  tripName: '', vehicleId: '', driverName: '', driverMobile: '',
  estStart: '', estEnd: '',
  fromLocation: PICKUP.location, fromLat: PICKUP.lat, fromLng: PICKUP.lng,
  toLocation: '', toLat: '', toLng: '',
  bagsCount: 0,
  challanCode: '', dispatchType: 'Retailer', dispatchCode: '',
  waybillNumber: '', invoiceNumber: '', quantityMt: '',
  ddCode: '', dealerCode: '', destStateCode: '', destDistrictCode: '',
  destStateName: '', destDistrictName: '',
  ...FIXED_CODES,
};

/** ISO instant → value for <input type="datetime-local"> in the user's own timezone. */
function toLocalInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** datetime-local value (local wall-clock) → ISO instant; '' stays ''. */
function localInputToIso(local: string): string {
  if (!local) return '';
  const d = new Date(local);            // parsed as LOCAL time
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}

export default function CreateTripPage() {
  const [idleVehicles, setIdleVehicles] = useState<Vehicle[]>([]);
  const [hiddenVeh, setHiddenVeh] = useState(0);                          // idle, but not on the IFMS whitelist
  const [tripVehicle, setTripVehicle] = useState<Vehicle | null>(null);   // edit mode: the trip's own truck
  const [vehLoadErr, setVehLoadErr] = useState(false);
  // Only IDLE trucks can take a trip. In edit mode the trip's own truck (ON_TRIP)
  // is added back. Derived, so it is correct whichever request finishes first.
  const vehicles = useMemo(
    () => (tripVehicle && !idleVehicles.some((v) => v.id === tripVehicle.id) ? [tripVehicle, ...idleVehicles] : idleVehicles),
    [idleVehicles, tripVehicle],
  );
  const [form, setForm] = useState<TripForm>(BLANK);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const { id: editId } = useParams();          // present → edit mode
  const isEdit = !!editId;
  const isMobile = useIsMobile();
  const gridStyle = { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14 };

  // Searchable vehicle picker (178+ trucks → plain <select> is unusable).
  const [vehSearch, setVehSearch] = useState('');
  const [vehOpen, setVehOpen] = useState(false);
  const vehRef = React.useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (vehRef.current && !vehRef.current.contains(e.target as Node)) setVehOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // Dealer picker — the destination comes from the vendor master, never typed.
  const [dealer, setDealer] = useState<Vendor | null>(null);
  const [dlrSearch, setDlrSearch] = useState('');
  const [dlrOpen, setDlrOpen] = useState(false);
  const [dlrRows, setDlrRows] = useState<Vendor[]>([]);
  const [dlrLoading, setDlrLoading] = useState(false);
  const dlrRef = React.useRef<HTMLDivElement>(null);
  const dlrSeq = React.useRef(0);
  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (dlrRef.current && !dlrRef.current.contains(e.target as Node)) setDlrOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // The master runs to lakhs of rows, so the search is done on the server after a
  // short pause; a slow older reply must never overwrite a newer one.
  useEffect(() => {
    if (!dlrOpen) return;
    const t = setTimeout(() => {
      const mine = ++dlrSeq.current;
      setDlrLoading(true);
      getVendors({ q: dlrSearch.trim() || undefined, limit: 20 })
        .then((r) => { if (mine === dlrSeq.current) setDlrRows(r.data.rows); })
        .catch(() => { if (mine === dlrSeq.current) setDlrRows([]); })
        .finally(() => { if (mine === dlrSeq.current) setDlrLoading(false); });
    }, 300);
    return () => clearTimeout(t);
  }, [dlrSearch, dlrOpen]);

  useEffect(() => {
    getVehicles()
      .then((r) => {
        const idle = (r.data.vehicles || []).filter((v) => v.status === 'IDLE');
        // IFMS rejects a trip whose truck is not on the VLTS whitelist, so only
        // whitelisted trucks can be dispatched from here at all.
        const listed = idle.filter((v) => v.ifmsWhitelisted === true);
        setIdleVehicles(listed);
        setHiddenVeh(idle.length - listed.length);
        setVehLoadErr(false);
      })
      .catch(() => setVehLoadErr(true));
  }, []);

  // Edit mode → load the trip and prefill the form.
  useEffect(() => {
    if (!isEdit || !editId) return;
    getTrip(editId).then(r => {
      const t = r.data.trip; if (!t) return;
      const dt = (v: ISODate | null | undefined) => (v ? toLocalInput(v) : '');
      setForm({
        tripName: t.tripName || '', vehicleId: t.vehicleId || '',
        driverName: t.driverName || '', driverMobile: t.driverMobile || '',
        estStart: dt(t.estStart), estEnd: dt(t.estEnd),
        fromLocation: t.fromLocation || PICKUP.location, fromLat: t.fromLat ?? PICKUP.lat, fromLng: t.fromLng ?? PICKUP.lng,
        toLocation: t.toLocation || '', toLat: t.toLat ?? '', toLng: t.toLng ?? '',
        bagsCount: t.bagsCount ?? 0,
        challanCode: t.challanCode || '', dispatchType: t.dispatchType || 'Retailer',
        dispatchCode: t.dispatchCode || '',
        waybillNumber: t.waybillNumber || '', invoiceNumber: t.invoiceNumber || '',
        quantityMt: t.quantityMt ?? '',
        ddCode: t.ddCode || '', dealerCode: t.dealerCode || '',
        destStateCode: t.destStateCode || '', destDistrictCode: t.destDistrictCode || '',
        // The trip stores codes, not names; picking the dealer again fills the names in.
        destStateName: '', destDistrictName: '',
        ...FIXED_CODES,   // plant codes are constant, never taken from the record
      });
      // ensure the current vehicle is selectable in edit mode
      if (t.vehicle) setTripVehicle(t.vehicle);
    }).catch(() => setErr('Failed to load trip'));
  }, [isEdit, editId]);

  const set = (k: TextField) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setForm(f => ({ ...f, [k]: e.target.value }));

  const onVehicle = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value;
    const v = vehicles.find(x => x.id === id);
    setForm(f => ({ ...f, vehicleId: id, driverName: v?.driverName || f.driverName, driverMobile: v?.driverMobile || f.driverMobile }));
  };
  const pickVehicle = (v: Vehicle) => {
    const dn = v.driverName && v.driverName !== '—' ? v.driverName : '';
    const dm = v.driverMobile && v.driverMobile !== '—' ? v.driverMobile : '';
    setForm(f => ({ ...f, vehicleId: v.id, driverName: dn || f.driverName, driverMobile: dm || f.driverMobile }));
    setVehSearch(''); setVehOpen(false);
  };
  const selectedVeh = vehicles.find(v => v.id === form.vehicleId);
  const vehFiltered = vehicles.filter(v => {
    const q = vehSearch.trim().toLowerCase();
    if (!q) return true;
    return `${v.vehicleNumber} ${v.driverName || ''}`.toLowerCase().includes(q);
  });

  const pickDealer = (v: Vendor) => {
    const geo = hasCoords(v);
    setDealer(v);
    setForm(f => ({
      ...f,
      dealerCode: v.code,
      toLocation: vendorPlace(v),
      toLat: geo ? String(v.latitude) : '',
      toLng: geo ? String(v.longitude) : '',
      // The LGD codes IFMS reports against are the dealer's own, never typed
      // here: Master → Vendor resolves them against the government list.
      destStateCode: v.stateCode ?? '',
      destDistrictCode: v.districtCode ?? '',
      destStateName: v.stateName ?? '',
      destDistrictName: v.districtName ?? '',
      dispatchType: v.type === 'WHOLESALER' ? 'Dealer' : 'Retailer',
    }));
    setDlrSearch(''); setDlrOpen(false);
  };

  // "Change" — drop the dealer and everything it filled, the destination codes
  // included: they describe that dealer and nothing else.
  const clearDealer = () => {
    setDealer(null);
    setForm(f => ({ ...f, toLocation: '', toLat: '', toLng: '',
      destStateCode: '', destDistrictCode: '', destStateName: '', destDistrictName: '' }));
    setDlrSearch(''); setDlrOpen(true);
  };

  // A dealer whose master row carries no LGD codes: the trip could be raised, but
  // IFMS would reject it, so this one is blocked rather than warned about.
  const missingDest = !!dealer && !(form.destStateCode && form.destDistrictCode);

  // A dealer the master has no map point for: the trip can be raised, but nothing
  // can be tracked against it until Master → Vendor is fixed.
  const dealerNoGeo = !!dealer && !hasCoords(dealer);

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault(); setErr('');
    // The vehicle picker is a search box, not a native <select>, so the browser's
    // own "required" check cannot see it — validate the chosen id here.
    if (!form.vehicleId) {
      setErr('Please select a vehicle.'); setVehOpen(true); return;
    }
    // Every remaining field is mandatory — catch any the browser let through.
    const REQUIRED: Partial<Record<keyof TripForm, string>> = {
      tripName: 'Trip Name', driverName: 'Driver Name', driverMobile: 'Driver Mobile',
      estStart: 'Estimate Start Date & Time', estEnd: 'Estimate End Date & Time',
      toLocation: 'Drop Location',
      challanCode: 'Challan Code', dispatchType: 'Dispatch Type', 
      waybillNumber: 'Waybill Number', invoiceNumber: 'Invoice Number',
      quantityMt: 'Quantity Dispatched (MT)',
      ddCode: 'Dispatch Code (DD)', dealerCode: 'Dealer Code',
    };
    const missing = Object.entries(REQUIRED)
      .filter(([k]) => String(form[k as keyof TripForm] ?? '').trim() === '')
      .map(([, label]) => label);
    if (missing.length) { setErr(`Please fill: ${missing.join(', ')}`); return; }
    // The destination codes are not on this form, so a dealer missing them has to
    // be fixed in the master — saying so here is more use than an IFMS rejection
    // discovered a day later.
    if (!form.destStateCode || !form.destDistrictCode) {
      setErr(`${dealer ? dealer.name : 'This dealer'} has no LGD state or district code in Master → Vendor. IFMS needs both, so add them there first.`);
      return;
    }

    // Guard: reject coordinates outside India (stops junk points landing abroad).
    if (!isInIndia(form.fromLat, form.fromLng)) {
      setErr('The fixed pickup point has invalid coordinates. Please contact the administrator.'); return;
    }
    setSaving(true);
    let { toLat, toLng } = form;
    // Dealers whose master row carries no coordinates: try to place the typed
    // drop location on the map ourselves before giving up.
    if (!isInIndia(toLat, toLng)) {
      const g = await geocodeAddress(form.toLocation);
      if (g) { toLat = g.lat; toLng = g.lng; setForm(f => ({ ...f, toLat: g.lat, toLng: g.lng })); }
    }
    if (!isInIndia(toLat, toLng)) {
      setSaving(false);
      setErr(`"${form.toLocation}" could not be placed on the map. Add the dealer's latitude and longitude in Master → Vendor, or type a nearby place name.`);
      return;
    }
    try {
      // <input type="datetime-local"> gives local wall-clock time without a zone;
      // send a real ISO instant so the server doesn't read it as UTC.
      const payload: TripForm = { ...form, toLat, toLng, estStart: localInputToIso(form.estStart), estEnd: localInputToIso(form.estEnd) };
      const r = editId ? await updateTrip(editId, payload) : await createTrip(payload);
      navigate(`/fleet/trips/${isEdit ? editId : r.data.trip.id}`);
    } catch (e2) { setErr((axios.isAxiosError<ApiErrorBody>(e2) ? e2.response?.data?.error : undefined) || (isEdit ? 'Failed to update trip' : 'Failed to create trip')); }
    finally { setSaving(false); }
  };

  const wide: React.CSSProperties = { gridColumn: isMobile ? 'auto' : '1 / -1' };

  return (
    <div style={{ ...P.page, maxWidth: 980 }}>
      <div style={P.head}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={S.titleIcon}><Icon name={isEdit ? 'edit' : 'plus'} size={18} stroke={2} /></div>
          <div>
            <h1 style={P.h1}>{isEdit ? 'Edit Trip' : 'Create Trip'}</h1>
            <p style={P.sub}>{isEdit ? 'Update trip details' : 'Dispatch a vehicle with full consignment details'}</p>
          </div>
        </div>
        <button type="button" style={P.btnGhost} onClick={() => navigate('/fleet/trips')}><Icon name="back" size={14} stroke={2.2} /> Trips</button>
      </div>

      <form onSubmit={submit}>
        {/* ── Trip & vehicle ── */}
        <Panel icon="truck" title="Trip & Vehicle" sub="What is being dispatched and on which truck">
        <div style={gridStyle}>
          <Field label="Trip Name" req><input style={P.input} value={form.tripName} onChange={set('tripName')} placeholder="Enter Trip Name" required /></Field>
          <Field label="Vehicle" req>
            <div style={{ position: 'relative' }} ref={vehRef}>
              <input
                style={P.input}
                value={vehOpen ? vehSearch : (selectedVeh ? selectedVeh.vehicleNumber : '')}
                onChange={e => { setVehSearch(e.target.value); setVehOpen(true); }}
                onFocus={() => { setVehSearch(''); setVehOpen(true); }}
                placeholder="🔍 Search vehicle number…"
                autoComplete="off"
              />
              <span style={S.vehCaret}>▾</span>
              {vehOpen && (
                <div style={S.vehMenu}>
                  {vehFiltered.length === 0 && <div style={S.vehEmpty}>No matching vehicle</div>}
                  {/* Every match is listed — the menu scrolls. Capping it at 50 meant a
                      truck could be in the fleet, match what was typed, and still not
                      appear, which reads exactly like the truck is missing. The count
                      stays pinned while scrolling so a long list is obviously long. */}
                  {vehFiltered.length > 7 && (
                    <div style={S.vehCount}>{vehFiltered.length} trucks — scroll, or type to narrow</div>
                  )}
                  {vehFiltered.map(v => (
                    <div
                      key={v.id}
                      style={{ ...S.vehItem, ...(v.id === form.vehicleId ? S.vehItemSel : {}) }}
                      onMouseDown={() => pickVehicle(v)}
                    >
                      <b>{v.vehicleNumber}</b>
                      {/* Which company's GPS this truck is on. Three feeds run side by
                          side and they fail in different ways, so the name belongs next
                          to the number — "no GPS ever" means something quite different
                          on a truck with no provider set at all. */}
                      <span style={v.gpsProvider ? S.vehProvider : S.vehProviderNone}>
                        {providerName(v.gpsProvider) || 'no GPS provider set'}
                      </span>
                      {v.driverName && v.driverName !== '—' ? <span style={S.vehDriver}> — {v.driverName}</span> : null}
                      {v.ifmsWhitelisted === false && <span style={S.vehFlag}>not on IFMS list</span>}
                      {gpsState(v) && (
                        <span style={gpsState(v)!.level === 'never' ? S.vehFlagRed : S.vehFlagGrey}>{gpsState(v)!.label}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {vehLoadErr
              ? <div style={S.warn}>Could not load vehicles. Please check the connection and reload.</div>
              : vehicles.length === 0 && (
                <div style={S.warn}>
                  {hiddenVeh > 0
                    ? `No IFMS-whitelisted vehicle is free — ${hiddenVeh} idle truck${hiddenVeh > 1 ? 's are' : ' is'} not on the whitelist.`
                    : 'No idle vehicles — all are on trips.'}
                </div>
              )}
            {vehicles.length > 0 && hiddenVeh > 0 && (
              <div style={S.hint}>Showing IFMS-whitelisted trucks only — {hiddenVeh} other idle truck{hiddenVeh > 1 ? 's are' : ' is'} hidden.</div>
            )}
            {/* VLTS only accepts a trip if the truck is on its whitelist, so say so
                before the dispatch is made rather than at the first IFMS failure. */}
            {selectedVeh && gpsState(selectedVeh)?.level === 'never' && (
              <div style={S.errBox}>
                <b>No GPS has ever been received for {selectedVeh.vehicleNumber}.</b> This is not a parked
                truck — the provider has never sent us a single position, so the truck is most likely not
                enabled on our GPS account. The trip can still be created, but it cannot auto-start on
                departure and IFMS will not receive Trip Start until the feed works.
              </div>
            )}
            {selectedVeh && gpsState(selectedVeh)?.level === 'quiet' && (
              <div style={S.hint}>
                Last position {gpsState(selectedVeh)!.label.replace('quiet ', '')} ago. Usually just a parked
                truck or a device that is switched off — it will resume on its own once the truck moves.
              </div>
            )}
            {selectedVeh && selectedVeh.ifmsWhitelisted === false && (
              <div style={S.warnBox}>
                <b>{selectedVeh.vehicleNumber} is not on the IFMS whitelist.</b> The trip will be created and
                tracked as usual, but IFMS is likely to reject Trip Start until VLTS whitelists this truck.
              </div>
            )}
          </Field>
        </div>
        </Panel>

        {/* ── Driver ── */}
        <Panel icon="users" title="Driver" sub="Filled from the vehicle, and can be corrected here">
        <div style={gridStyle}>
          <Field label="Driver Name" req><input style={P.input} value={form.driverName} onChange={set('driverName')} placeholder="Enter Driver Name" required /></Field>
          <Field label="Driver Mobile" req><input style={P.input} value={form.driverMobile} onChange={set('driverMobile')} placeholder="Enter Driver Phone" required /></Field>
        </div>
        </Panel>

        {/* ── Schedule ── */}
        <Panel icon="clock" title="Schedule" sub="Planned departure and arrival — used for the delay report">
        <div style={gridStyle}>
          <Field label="Estimate Start Date & Time" req><input style={P.input} type="datetime-local" value={form.estStart} onChange={set('estStart')} required /></Field>
          <Field label="Estimate End Date & Time" req><input style={P.input} type="datetime-local" value={form.estEnd} onChange={set('estEnd')} required /></Field>
        </div>
        </Panel>

        {/* ── Route ── */}
        <Panel icon="pin" title="Route" sub="Every dispatch leaves the plant; the drop comes from the vendor master">
        <div style={gridStyle}>
        {/* Pickup is always the plant — shown for confirmation, not editable. */}
        <div style={wide}>
        <Field label="Pickup Location" auto>
          <div style={S.fixedBox}>
            <span style={S.fixedIcon}>🏭</span>
            <div style={S.fixedText}>{form.fromLocation}</div>
          </div>
        </Field>
        </div>
        <Field label="Dealer / Vendor" req>
          <div style={{ position: 'relative' }} ref={dlrRef}>
            <input
              style={P.input}
              value={dlrOpen ? dlrSearch : (dealer ? dealer.name : '')}
              onChange={e => { setDlrSearch(e.target.value); setDlrOpen(true); }}
              onFocus={() => { setDlrSearch(''); setDlrOpen(true); }}
              onKeyDown={e => {
                if (e.key === 'Escape') { setDlrOpen(false); return; }
                // Enter would submit the half-filled form — take the top match instead.
                if (e.key === 'Enter' && dlrOpen) { e.preventDefault(); if (dlrRows[0]) pickDealer(dlrRows[0]); }
              }}
              placeholder="🔍 Search dealer code or name…"
              autoComplete="off"
            />
            <span style={S.vehCaret}>▾</span>
            {dlrOpen && (
              <div style={S.vehMenu}>
                {dlrLoading && dlrRows.length === 0 && <div style={S.vehEmpty}>Searching…</div>}
                {!dlrLoading && dlrRows.length === 0 && <div style={S.vehEmpty}>No matching dealer</div>}
                {dlrRows.map(v => (
                  <div
                    key={v.id}
                    style={{ ...S.vehItem, ...(v.id === dealer?.id ? S.vehItemSel : {}) }}
                    onMouseDown={() => pickDealer(v)}
                  >
                    <b>{v.name}</b>
                    <div style={S.dlrSub}>{[v.code, v.districtName, v.stateName].filter(Boolean).join(' · ')}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {dealer && (
            <div style={S.dlrPick}>
              <span>{[dealer.name, dealer.code, dealer.districtName].filter(Boolean).join(' · ')}</span>
              <button type="button" style={S.dlrChange} onClick={clearDealer}>Change</button>
            </div>
          )}
        </Field>
        <Field label="Drop Location" req auto={!!dealer && !dealerNoGeo}>
          <input
            style={{ ...P.input, ...(dealer && !dealerNoGeo ? S.readOnly : {}) }}
            value={form.toLocation}
            onChange={set('toLocation')}
            readOnly={!!dealer && !dealerNoGeo}
            placeholder="Choose a dealer above"
            required
          />
          <div style={S.hint}>
            {dealerNoGeo
              ? <span style={S.warnText}>⚠ This dealer has no map location in the master, so the trip cannot be tracked to it. Add its latitude and longitude in Master → Vendor. Until then you may edit the drop location above and we will try to place it on the map.</span>
              : (form.toLat && form.toLng
                  ? <span style={S.okText}>✓ Location pinned on map</span>
                  : 'Filled from the dealer you choose above.')}
          </div>
        </Field>
        </div>
        </Panel>

        {/* ── Other details ── */}
        <Panel icon="database" title="Consignment & IFMS codes" sub="Other Details — the dispatch paperwork IFMS receives">
        <div style={gridStyle}>
          <Field label="Challan Code" req><input style={P.input} value={form.challanCode} onChange={set('challanCode')} placeholder="Enter Challan Code" required /></Field>
          <Field label="Dispatch Type" req>
            <select style={P.input} value={form.dispatchType} onChange={set('dispatchType')} required>
              {DISPATCH_TYPES.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </Field>

          {/* These three never change for this plant — shown read-only. */}
          <div style={{ ...wide, ...S.fixedRow, gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr' }}>
            <Field label="Company Code" auto><div style={S.fixedVal}>{FIXED_CODES.companyCode}</div></Field>
            <Field label="Plant Or Port Code" auto><div style={S.fixedVal}>{FIXED_CODES.plantPortCode}</div></Field>
            <Field label="Product Code" auto><div style={S.fixedVal}>{FIXED_CODES.productCode}</div></Field>
          </div>

          <Field label="Waybill Number" req><input style={P.input} value={form.waybillNumber} onChange={set('waybillNumber')} placeholder="Enter Waybill Number" required /></Field>
          <Field label="Invoice Number" req><input style={P.input} value={form.invoiceNumber} onChange={set('invoiceNumber')} placeholder="Enter Invoice Number" required /></Field>

          <Field label="Quantity Dispatched (MT)" req><input style={P.input} type="number" value={form.quantityMt} onChange={set('quantityMt')} placeholder="Enter Quantity (MT)" required /></Field>

          {/* Same labels/placeholders as the Update Trip modal, so both screens agree. */}
          <Field label="Dispatch Code (DD)" req><input style={P.input} value={form.ddCode} onChange={set('ddCode')} placeholder="e.g. 100158080430" required /></Field>
          <Field label="Dealer Code" req><input style={P.input} value={form.dealerCode} onChange={set('dealerCode')} placeholder="e.g. 1642243" required /></Field>
          {/* Not typed. The destination LGD codes are the dealer's own, resolved
              against the government list when the dealer is added in Master →
              Vendor. They are shown because they are what IFMS receives. */}
          <div style={{ ...wide, ...S.fixedRow, gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr' }}>
            <Field label="Destination State" auto>
              <div style={S.fixedVal}>{destLabel(form.destStateName, form.destStateCode)}</div>
            </Field>
            <Field label="Destination District" auto>
              <div style={S.fixedVal}>{destLabel(form.destDistrictName, form.destDistrictCode)}</div>
            </Field>
          </div>
        </div>
        <div style={missingDest
          ? { ...S.note, color: 'var(--fx-orange)', background: 'var(--fx-orange-soft)' }
          : S.note}>
          {missingDest
            ? `⚠ ${dealer ? dealer.name : 'This dealer'} has no LGD state or district code in Master → Vendor. IFMS rejects a trip without them, so fix the dealer there before creating this trip.`
            : 'These codes are what IFMS receives. The state and district come from the dealer\u2019s master record \u2014 correct them in Master \u2192 Vendor, not here.'}
        </div>
        </Panel>

        {err && <div role="alert" style={S.err}>{err}</div>}

        <div style={S.foot}>
          <button type="button" style={S.footCancel} onClick={() => navigate('/fleet/trips')}>Cancel</button>
          {isMobile
            ? <span style={{ flex: 1 }} />
            : <span style={S.footHint}>{isEdit ? 'Changes are saved to this trip only.' : 'The trip starts tracking as soon as the truck leaves the plant.'}</span>}
          <button style={{ ...P.btnPrimary, ...S.footSubmit, opacity: saving ? 0.7 : 1 }} disabled={saving}>
            {saving ? (isEdit ? 'Saving…' : 'Creating…') : (isEdit ? '💾 Save Changes' : 'Submit — Create Trip →')}
          </button>
        </div>
      </form>
    </div>
  );
}

/** One titled block of the form: small icon tile, heading, and the fields under it. */
function Panel({ icon, title, sub, children }: { icon: IconName; title: string; sub?: string; children: React.ReactNode }) {
  return (
    <section style={S.panel}>
      <div style={S.panelHead}>
        <span style={S.panelIcon}><Icon name={icon} size={15} stroke={2.2} /></span>
        <div>
          <div style={S.panelTitle}>{title}</div>
          {sub && <div style={S.panelSub}>{sub}</div>}
        </div>
      </div>
      <div style={S.panelBody}>{children}</div>
    </section>
  );
}

function Field({ label, children, req, auto }: { label: string; children: React.ReactNode; req?: boolean; auto?: boolean }) {
  return (
    <div style={S.field}>
      <label style={S.fieldLabel}>
        <span>{label}{req && <span style={S.req}> *</span>}</span>
        {auto && <span style={S.autoTag}>🔒 auto</span>}
      </label>
      {children}
    </div>
  );
}

const S = css({
  titleIcon: { width: 38, height: 38, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flex: 'none', background: 'var(--fx-accent)', boxShadow: '0 2px 6px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.3)' },
  // No `overflow: hidden` — the vehicle / dealer menus drop out of the panel.
  panel: { background: 'var(--fx-surface)', border: '1px solid var(--fx-border)', borderRadius: 14, boxShadow: 'var(--fx-shadow)', marginBottom: 14 },
  panelHead: { display: 'flex', alignItems: 'center', gap: 10, padding: '13px 18px', borderBottom: '1px solid var(--fx-border)', background: 'var(--fx-surface-2)', borderRadius: '13px 13px 0 0' },
  panelIcon: { width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none', color: 'var(--fx-accent)', background: 'var(--fx-accent-soft)' },
  panelTitle: { fontSize: 14, fontWeight: 700, color: 'var(--fx-text)', letterSpacing: '-0.01em' },
  panelSub: { fontSize: 11.5, color: 'var(--fx-text-2)', marginTop: 1 },
  panelBody: { padding: '16px 18px 18px' },
  field: { minWidth: 0 },
  fieldLabel: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: 12, fontWeight: 500, color: 'var(--fx-text-2)', marginBottom: 5 },
  req: { color: 'var(--fx-red)', fontWeight: 700 },
  autoTag: { fontSize: 10, fontWeight: 600, color: 'var(--fx-text-3)', background: 'var(--fx-gray-soft)', borderRadius: 20, padding: '1px 7px', whiteSpace: 'nowrap' },
  fixedRow: { display: 'grid', gap: 14 },
  warn: { fontSize: 12, color: 'var(--fx-orange)', marginTop: 4 },
  warnBox: {
    fontSize: 11.5, lineHeight: 1.5, color: 'var(--fx-orange)', background: 'var(--fx-orange-soft)',
    border: '1px solid var(--fx-orange)', borderRadius: 10, padding: '8px 11px', marginTop: 7,
  },
  errBox: {
    fontSize: 11.5, lineHeight: 1.5, color: 'var(--fx-red)', background: 'var(--fx-red-soft)',
    border: '1px solid var(--fx-red)', borderRadius: 10, padding: '8px 11px', marginTop: 7,
  },
  vehProvider: {
    fontSize: 10, fontWeight: 600, color: 'var(--fx-text-2)', border: '1px solid var(--fx-border)',
    borderRadius: 20, padding: '1px 7px', marginLeft: 8, whiteSpace: 'nowrap',
  },
  vehProviderNone: {
    fontSize: 10, fontWeight: 600, color: 'var(--fx-orange)', background: 'var(--fx-orange-soft)',
    borderRadius: 20, padding: '1px 7px', marginLeft: 8, whiteSpace: 'nowrap',
  },
  vehFlagGrey: {
    fontSize: 10, fontWeight: 600, color: 'var(--fx-text-3)', background: 'var(--fx-gray-soft)',
    borderRadius: 20, padding: '1px 7px', marginLeft: 8, whiteSpace: 'nowrap',
  },
  vehFlagRed: {
    fontSize: 10, fontWeight: 700, color: 'var(--fx-red)', background: 'var(--fx-red-soft)',
    borderRadius: 20, padding: '1px 7px', marginLeft: 8, whiteSpace: 'nowrap',
  },
  vehFlag: {
    fontSize: 10, fontWeight: 700, color: 'var(--fx-orange)', background: 'var(--fx-orange-soft)',
    borderRadius: 20, padding: '1px 7px', marginLeft: 8, whiteSpace: 'nowrap',
  },
  warnText: { color: 'var(--fx-orange)', fontWeight: 600 },
  okText: { color: 'var(--fx-green)', fontWeight: 600 },
  hint: { fontSize: 11.5, color: 'var(--fx-text-2)', marginTop: 5 },
  note: { fontSize: 11.5, color: 'var(--fx-text-2)', lineHeight: 1.5, background: 'var(--fx-accent-soft)', borderRadius: 10, padding: '9px 12px', margin: '14px 0 0' },
  err: { color: 'var(--fx-red)', background: 'var(--fx-red-soft)', border: '1px solid var(--fx-border)', borderRadius: 10, fontSize: 12.5, padding: '9px 13px', marginBottom: 12 },
  // Sits at the end of the form, not stuck to the viewport: a floating bar would
  // cover the fields it hovers over (and the dealer menu) on short screens.
  foot: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '12px 16px', borderRadius: 14, border: '1px solid var(--fx-border)', background: 'var(--fx-surface)', boxShadow: 'var(--fx-shadow)' },
  footCancel: { background: 'transparent', border: 'none', padding: '7px 4px', fontSize: 13, fontWeight: 600, color: 'var(--fx-text-2)', cursor: 'pointer', fontFamily: 'inherit' },
  footHint: { flex: 1, minWidth: 120, fontSize: 11.5, color: 'var(--fx-text-3)' },
  footSubmit: { padding: '9px 20px', fontSize: 13.5 },
  readOnly: { background: 'var(--fx-surface-2)', color: 'var(--fx-text-2)', cursor: 'default' },
  dlrSub: { marginTop: 2, fontSize: 11.5, color: 'var(--fx-text-2)' },
  dlrPick: { display: 'flex', alignItems: 'center', gap: 10, marginTop: 6, fontSize: 12, color: 'var(--fx-text-2)' },
  dlrChange: { background: 'none', border: 'none', padding: 0, font: 'inherit', color: P.navy, fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' },
  vehCaret: { position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--fx-text-2)', fontSize: 12, pointerEvents: 'none' },
  vehMenu: { position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 30, background: 'var(--fx-surface)', border: '1px solid var(--fx-border)', borderRadius: 10, boxShadow: 'var(--fx-shadow-lg)', maxHeight: 260, overflowY: 'auto' },
  vehItem: { padding: '8px 12px', fontSize: 12.5, color: 'var(--fx-text)', cursor: 'pointer', borderBottom: '1px solid var(--fx-border)' },
  vehItemSel: { background: 'var(--fx-surface-2)' },
  vehDriver: { color: 'var(--fx-text-2)', fontSize: 11.5 },
  vehEmpty: { padding: '10px 12px', fontSize: 12, color: 'var(--fx-text-2)' },
  vehCount: {
    position: 'sticky', top: 0, zIndex: 1, padding: '7px 12px', fontSize: 11,
    fontWeight: 600, color: 'var(--fx-text-2)', background: 'var(--fx-surface-2)',
    borderBottom: '1px solid var(--fx-border)',
  },
  fixedBox: { display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', background: 'var(--fx-surface-2)', border: '1px solid var(--fx-border)', borderRadius: 10 },
  fixedIcon: { fontSize: 16, lineHeight: 1 },
  fixedText: { flex: 1, fontSize: 12.5, fontWeight: 500, color: 'var(--fx-text)', lineHeight: 1.35 },
  fixedVal: { padding: '8px 11px', background: 'var(--fx-surface-2)', border: '1px solid var(--fx-border)', borderRadius: 8, fontSize: 13, fontWeight: 600, color: 'var(--fx-text)' },
});
